import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, Save, Star, Users, Edit2, Infinity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { checkSubscriptionStatus } from '../../lib/payments';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { format } from 'date-fns';
import { trackEvent, trackException, setUserProperties } from '../../lib/analytics';

const Profile: React.FC = () => {
  const { user, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    gender: 'male',
    email: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    birthCountry: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  useEffect(() => {
    // Track page visit
    trackEvent('profile_page', 'visit', userProfile?.is_premium ? 'premium_user' : 'free_user');
  }, [userProfile?.is_premium]);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || '',
        gender: userProfile.gender || 'male',
        email: userProfile.email || '',
        birthDate: userProfile.birth_date || '',
        birthTime: userProfile.birth_time || '',
        birthPlace: userProfile.birth_location?.city || '',
        birthCountry: userProfile.birth_location?.country || 'India'
      });

      // Fetch subscription details if user is premium
      const fetchSubscriptionDetails = async () => {
        if (user?.phoneNumber && userProfile?.is_premium) {
          try {
            const subscription = await checkSubscriptionStatus(user.phoneNumber);
            setSubscriptionDetails(subscription);
            
            trackEvent('profile_page', 'subscription_loaded', subscription ? 'active' : 'none');
          } catch (error: any) {
            console.error('Error fetching subscription details:', error);
            trackEvent('profile_page', 'subscription_error', 'fetch_failed');
          } finally {
            setLoadingSubscription(false);
          }
        } else {
          setLoadingSubscription(false);
        }
      };

      fetchSubscriptionDetails();
    }
  }, [userProfile, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (success) setSuccess(false);
    
    // Track form field interactions
    trackEvent('profile_edit', 'field_change', name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    trackEvent('profile_edit', 'attempt', 'save_changes');
    
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      await updateUserProfile({
        name: formData.name,
        gender: formData.gender,
        email: formData.email || undefined,
        birth_date: formData.birthDate,
        birth_time: formData.birthTime,
        birth_location: {
          city: formData.birthPlace,
          country: formData.birthCountry
        }
      });
      
      // Update user properties in GA
      setUserProperties({
        user_gender: formData.gender,
        user_country: formData.birthCountry,
        profile_completed: true,
        has_email: !!formData.email
      });
      
      setSuccess(true);
      setIsEditing(false);
      
      trackEvent('profile_edit', 'success', 'profile_updated');
      
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
      
      trackEvent('profile_edit', 'error', 'update_failed');
      trackException(`Profile update failed: ${err.message}`, false);
    } finally {
      setLoading(false);
    }
  };

  const renderSubscriptionStatus = () => {
    if (!userProfile?.is_premium) {
      return (
        <div className="text-center">
          <p className="text-sm text-neutral-600 mb-3">
            Upgrade to premium for unlimited readings and exclusive features
          </p>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => {
              trackEvent('conversion', 'premium_from_profile', 'upgrade_button');
              navigate('/premium');
            }}
            leftIcon={<Star size={16} />}
          >
            Upgrade Now
          </Button>
        </div>
      );
    }

    if (loadingSubscription) {
      return (
        <div className="flex justify-center py-2">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-secondary-500"></div>
        </div>
      );
    }

    if (!subscriptionDetails) {
      return (
        <p className="text-sm text-neutral-600 text-center">
          Error loading subscription details
        </p>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
          <div className="flex items-center justify-center mb-2">
            <Star className="h-5 w-5 text-secondary-500 mr-1" />
            <span className="font-semibold text-secondary-800">Premium Active</span>
          </div>
          {subscriptionDetails.end_date ? (
            <>
              <p className="text-sm text-neutral-600 text-center mb-2">
                Valid until
              </p>
              <p className="text-lg font-semibold text-center text-primary-800">
                {format(new Date(subscriptionDetails.end_date), 'MMMM d, yyyy')}
              </p>
            </>
          ) : (
            <div className="flex items-center justify-center text-sm text-neutral-600">
              <Infinity className="h-4 w-4 mr-1" />
              <span>Lifetime Access</span>
            </div>
          )}
        </div>
        <div className="text-sm text-neutral-600">
          <div className="flex items-start mb-2">
            <Clock className="h-4 w-4 mr-2 mt-1 flex-shrink-0" />
            <span>Started on {format(new Date(subscriptionDetails.start_date), 'MMMM d, yyyy')}</span>
          </div>
          <div className="flex items-start">
            <Star className="h-4 w-4 mr-2 mt-1 flex-shrink-0" />
            <span>All premium features unlocked</span>
          </div>
        </div>
      </div>
    );
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left sidebar */}
          <div className="md:col-span-1">
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary-100 mb-4">
                    <User className="h-10 w-10 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold">{userProfile?.name || 'User'}</h3>
                  <p className="text-neutral-500 text-sm">{user.phoneNumber}</p>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Status Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center text-base">
                  <Star className="h-5 w-5 mr-2" />
                  Subscription Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderSubscriptionStatus()}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    leftIcon={<Clock size={16} />}
                    onClick={() => {
                      trackEvent('navigation', 'readings_from_profile', 'quick_action');
                      navigate('/readings');
                    }}
                  >
                    Reading History
                  </Button>
                  
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => {
                      trackEvent('navigation', 'dashboard_from_profile', 'quick_action');
                      navigate('/');
                    }}
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Main content */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Birth Details</CardTitle>
                  {!isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Edit2 size={16} />}
                      onClick={() => {
                        setIsEditing(true);
                        trackEvent('profile_edit', 'start', 'edit_button');
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit}>
                  <Input
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    leftIcon={<User size={18} className="text-neutral-500" />}
                    fullWidth
                    disabled={!isEditing || loading}
                  />
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Gender</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Users size={18} className="text-neutral-500" />
                      </div>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className="block rounded-lg border px-4 py-2 pl-10 w-full text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow border-neutral-300"
                        disabled={!isEditing || loading}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                  </div>
                  
                  <Input
                    label="Email (Optional)"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    fullWidth
                    disabled={!isEditing || loading}
                  />
                  
                  <Input
                    label="Date of Birth"
                    name="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={handleChange}
                    leftIcon={<Calendar size={18} className="text-neutral-500" />}
                    fullWidth
                    disabled={!isEditing || loading}
                  />
                  
                  <Input
                    label="Birth Time"
                    name="birthTime"
                    type="time"
                    value={formData.birthTime}
                    onChange={handleChange}
                    leftIcon={<Clock size={18} className="text-neutral-500" />}
                    helperText="As accurate as possible for better predictions"
                    fullWidth
                    disabled={!isEditing || loading}
                  />
                  
                  <Input
                    label="Birth Place"
                    name="birthPlace"
                    value={formData.birthPlace}
                    onChange={handleChange}
                    placeholder="City name"
                    leftIcon={<MapPin size={18} className="text-neutral-500" />}
                    fullWidth
                    disabled={!isEditing || loading}
                  />
                  
                  <Input
                    label="Country"
                    name="birthCountry"
                    value={formData.birthCountry}
                    onChange={handleChange}
                    placeholder="Country"
                    fullWidth
                    disabled={!isEditing || loading}
                  />
                  
                  {error && (
                    <div className="bg-error-50 text-error-600 p-3 rounded-md text-sm mt-4">
                      {error}
                    </div>
                  )}
                  
                  {success && (
                    <div className="bg-success-50 text-success-600 p-3 rounded-md text-sm mt-4">
                      Profile updated successfully! Redirecting to home...
                    </div>
                  )}
                  
                  {isEditing && (
                    <div className="flex gap-3 mt-6">
                      <Button
                        type="submit"
                        isLoading={loading}
                        leftIcon={<Save size={16} />}
                        fullWidth
                      >
                        Save Changes
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          trackEvent('profile_edit', 'cancel', 'cancel_button');
                        }}
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;